import { useEffect, useRef, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useViewsStore } from '@/stores';
import { isCounterpartyNotFound, useCounterpartyDetail } from '@/hooks/counterparty';
import { parsePositiveIntParam } from '@/utils/search-params';

/**
 * Syncs the browser URL ?counterparty=<id> param with the active counterparty
 * tab in the views store.
 *
 * LR-671: the param used to be `?view=`. It was renamed so that `?view_id=`
 * (the saved view — columns/pins/filters/sorting, shared naming with
 * /inventory) and the tab slice can coexist without confusion. It carried the
 * tab's slug until tabs got counterparty ids; `?view=` and a slug are still
 * read as aliases for one release (bookmarks, links pasted in chats) and get
 * rewritten to `?counterparty=<id>` on the next URL write.
 *
 * On mount: waits for store hydration, then reads the param from the URL and
 * activates the matching tab. An id without a tab is looked up and opened as a
 * tab (a deactivated counterparty's link works for anyone); an unknown one
 * shows a toast and falls back to All Trades. `tabListFirstPage` is the tab list's
 * first page (`undefined` until it is in, `[]` if it failed): an id on it gets its
 * auto tab, so it is not looked up.
 *
 * On activeViewId change: updates the URL via the router (replace, no new
 * history entry), preserving every sibling param — including `view_id`, so
 * switching tabs never resets the saved view.
 */
export function useUrlViewSync(
  showToast: (message: string) => void,
  tabListFirstPage: readonly { id: number }[] | undefined,
): void {
  const views = useViewsStore((s) => s.views);
  const activeViewId = useViewsStore((s) => s.activeViewId);
  const activate = useViewsStore((s) => s.activate);
  const openTab = useViewsStore((s) => s.openTab);
  const openCounterpartyTab = useViewsStore((s) => s.openCounterpartyTab);
  const navigate = useNavigate();

  // Read the path + navigation status from the ROUTER, not `window.location`.
  // During a pending SPA transition (e.g. opening /trades/$tradeId), the router's
  // `location` already reflects the target while `window.location.pathname` still
  // lags at `/trades` — so the stale-pathname guard in the on-switch effect below
  // used to fire a competing navigate({to:'/trades'}) and revert the trade page
  // (LR-208 / LR-446: clicking a trade ID, or the tour's single-trade step, "did
  // not open" — it opened, then this effect instantly bounced it back).
  const routerPath = useRouterState({ select: (s) => s.location.pathname });
  const isNavigating = useRouterState({ select: (s) => s.status === 'pending' });

  // Track whether this component is still mounted — prevents stale
  // replaceState calls from firing after the user navigates away from /trades.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Track hydration locally — zustand persist exposes hasHydrated() and onFinishHydration()
  const [hasHydrated, setHasHydrated] = useState(
    () => useViewsStore.persist.hasHydrated?.() ?? true
  );

  useEffect(() => {
    const onFinish = useViewsStore.persist.onFinishHydration;
    if (typeof onFinish === 'function') {
      const unsub = onFinish(() => setHasHydrated(true));
      return typeof unsub === 'function' ? unsub : undefined;
    }
    // If API not available, assume hydrated
    setHasHydrated(true);
  }, []);

  // Flag to prevent the "on switch" effect from firing during initial mount resolution
  const initialSyncDone = useRef<boolean>(false);
  // The ?counterparty= (or legacy ?view=) value captured once from the URL, pending
  // resolution: a counterparty id, or a legacy slug. `undefined` = not read yet;
  // `null` = no param present.
  const pendingRef = useRef<number | string | null | undefined>(undefined);
  // An id with no tab yet, looked up to open one. 0 keeps the lookup idle.
  const [lookupId, setLookupId] = useState(0);
  const lookup = useCounterpartyDetail(lookupId);

  // On mount (after hydration): read the URL param and resolve it to a view.
  // Re-runs on every `views` change until resolved — per-CP auto views are
  // created asynchronously (after the counterparties query resolves), so a
  // valid CP may have no tab on the first pass. We must wait for `views` to
  // grow rather than falsely reporting "not found" and giving up (which dropped
  // shared/bookmarked view links on a cold load).
  useEffect(() => {
    if (!hasHydrated) return;
    if (initialSyncDone.current) return;

    if (pendingRef.current === undefined) {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('counterparty') ?? params.get('view');
      pendingRef.current = raw ? (parsePositiveIntParam(raw) ?? raw) : null;
    }
    const wanted = pendingRef.current;

    // No param → nothing to resolve.
    if (wanted === null) {
      initialSyncDone.current = true;
      return;
    }

    if (typeof wanted === 'number') {
      const tab = views.find((v) => v.counterpartyId === wanted);
      if (tab) {
        openTab(tab.id);
        initialSyncDone.current = true;
      } else if (tabListFirstPage !== undefined && !tabListFirstPage.some((cp) => cp.id === wanted)) {
        setLookupId(wanted);
      }
      return;
    }

    const matchedView = views.find((v) => v.slug === wanted);
    if (matchedView) {
      openTab(matchedView.id);
      initialSyncDone.current = true;
      return;
    }

    // Slug not matched yet. Only treat it as genuinely unknown once auto views
    // exist (i.e. the counterparties list has loaded); until then, wait for the
    // next `views` update instead of showing a false "not found" toast.
    if (views.some((v) => v.isAutoGenerated)) {
      showToast('View not found. Showing All Trades.');
      activate('all-trades');
      initialSyncDone.current = true;
    }
  }, [hasHydrated, views, tabListFirstPage, activate, openTab, showToast]);

  useEffect(() => {
    if (!lookupId || initialSyncDone.current) return;
    if (lookup.data !== undefined && lookup.data.id === lookupId) {
      openCounterpartyTab({
        id: lookup.data.id,
        name: lookup.data.name,
        isActive: lookup.data.isActive,
      });
      initialSyncDone.current = true;
    } else if (lookup.isError) {
      showToast(
        isCounterpartyNotFound(lookup.error)
          ? 'View not found. Showing All Trades.'
          : 'Could not load this counterparty. Showing All Trades.',
      );
      activate('all-trades');
      initialSyncDone.current = true;
    }
  }, [lookupId, lookup.data, lookup.isError, lookup.error, openCounterpartyTab, activate, showToast]);

  // On activeViewId change: update the URL via the router so its internal
  // search state stays in sync and sibling params (mode/cp/htab/hview/sel and
  // the saved view's view_id) are preserved. (Raw history.replaceState rebuilt
  // the URL from scratch — dropping mode/cp — and left the router's parsed
  // search stale, so a later navigate() could lose the active view.)
  useEffect(() => {
    if (!initialSyncDone.current) return;
    if (!mountedRef.current) return;
    // Never fire a competing navigate while a route transition is in flight —
    // doing so cancels the pending transition (e.g. the one opening
    // /trades/$tradeId) and reverts it. The effect re-runs once the router goes
    // idle (status is a dep), at which point `routerPath` is the settled route
    // and the guard below decides correctly.
    if (isNavigating) return;
    // Only sync view→URL on the Trades LIST route itself. `startsWith('/trades')`
    // ALSO matched the single-trade detail route `/trades/$tradeId`, so this effect
    // fired there and navigated back to `/trades` — clobbering an in-app transition
    // to a trade (LR-208: clicking a trade ID "didn't open" the page — it opened, then
    // this effect instantly reverted it). MTM/History keep pathname `/trades` (the mode
    // lives in the search params), so they still sync correctly. Read the path from
    // the ROUTER (reflects the pending target during a transition) rather than the
    // lagging `window.location.pathname`.
    const path = routerPath;
    if (path !== '/trades' && path !== '/trades/') return;

    const activeView = views.find((v) => v.id === activeViewId);
    if (!activeView) return;

    // A tab persisted before ids keeps its slug until the tab sync gives it one.
    const nextView =
      activeView.id === 'all-trades' ? undefined : (activeView.counterpartyId ?? activeView.slug);

    // Idempotent + race-safe: if the URL already reflects the active view, do
    // nothing. Without this, an unrelated re-render during an in-flight SPA
    // transition to /trades/$tradeId re-ran this effect while pathname was still
    // `/trades`, firing navigate({ to: '/trades' }) — which cancelled the pending
    // transition, so clicking a trade ID "didn't open" the page (LR-208).
    const params = new URLSearchParams(window.location.search);
    const currentView = params.get('counterparty') || undefined;
    // A legacy `?view=` still in the address bar is migrated by the write below
    // even when it already names the active tab.
    const legacyView = params.get('view') || undefined;
    if (currentView === (nextView === undefined ? undefined : String(nextView)) && !legacyView) return;

    navigate({
      to: '/trades',
      replace: true,
      // Preserve the route's sibling params; only swap `counterparty`.
      // htab is History-scoped (LR-390 Trades/MTM subtab) — dropping it here
      // bounced the user off History → Mark to Market the moment auto-views
      // updated activeViewId after a fresh navigation. hview/sel (LR-498
      // History rail + selection) were dropped by the old 4-key whitelist —
      // kept now for the same reason. `view_id` (LR-671) is the saved view:
      // independent of the tab slice, so swapping the tab must not reset it
      // (the bootstrap keeps `counterparty` when it writes view_id, so neither
      // resets the other). The legacy `view` key is intentionally absent —
      // this write is what retires it from the address bar.
      search: (prev) => ({
        mode: prev.mode,
        cp: typeof prev.cp === 'number' ? prev.cp : undefined,
        htab: prev.htab,
        hview: prev.hview,
        sel: prev.sel,
        view_id: prev.view_id,
        counterparty: nextView,
      }),
    });
  }, [activeViewId, views, navigate, routerPath, isNavigating]);
}
